import { useForm } from '@tanstack/react-form';
import { ChangeEvent, PropsWithChildren, useMemo, useState } from 'react';
import z from 'zod';
import { EnrichedLifeglovLinerStock, useUpdateLifeglovLinerStockMutation } from '../../api/lifeglov-liner-stock';
import { useAuthentication } from '../../auth';
import { Button } from '../../components/Button';
import { Dialog, DialogClose, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../../components/Dialog';
import { NumberInput } from '../../components/Input/NumberInput';
import { useToaster } from '../../hooks/useToaster';
import { capitalizeFirstLetter } from '../../util';

interface EditLifeglovLinerStockProps extends PropsWithChildren {
  stock: EnrichedLifeglovLinerStock;
}

const formValuesSchema = z.object({
  count: z
    .number({ error: 'Shipped is required' })
    .int({ error: 'Shipped must be a whole number' })
    .min(0, { error: 'Shipped must be a non-negative integer' }),
  adjustment: z.number({ error: 'Adjustment is required' }).int({ error: 'Adjustment must be a whole number' })
});

export function EditLifeglovLinerStockDialog({ stock, children }: EditLifeglovLinerStockProps) {
  const [open, setOpen] = useState(false);
  const currentUser = useAuthentication();
  const [updateLifeglovLinerStock] = useUpdateLifeglovLinerStockMutation();
  const toast = useToaster();

  const form = useForm({
    defaultValues: {
      count: stock.count as number | null,
      adjustment: stock.adjustment as number | null
    },
    validators: {
      onChange: formValuesSchema
    },
    onSubmit: async ({ value: { count, adjustment } }) => {
      if (count === null || adjustment === null) {
        return;
      }

      try {
        await updateLifeglovLinerStock({ id: stock.id, count, adjustment }).unwrap();
        toast.success('Liner stock updated successfully.');
        setOpen(false);
      } catch {
        toast.error('Failed to update liner stock. Please try again.');
      }
    }
  });

  const title = useMemo(() => {
    const clinic = currentUser?.subtenants?.find((s) => s.name === stock.subtenantName);
    const clinicLabel = clinic?.label ?? stock.subtenantName;
    const typeLabel = `${capitalizeFirstLetter(stock.hand)}/${capitalizeFirstLetter(stock.size)}`;
    return `Edit ${typeLabel} Liner Stock for ${clinicLabel}`;
  }, [stock, currentUser]);

  return (
    <Dialog
      open={open}
      onOpenChange={(open) => {
        form.reset();
        setOpen(open);
      }}
    >
      {children}

      <DialogContent>
        <DialogHeader>
          <DialogTitle className="leading-6">{title}</DialogTitle>
        </DialogHeader>

        <div className="flex flex-col gap-4">
          <form.Field name="count">
            {(field) => (
              <NumberInput
                label="Shipped"
                name={field.name}
                value={field.state.value ?? undefined}
                onChange={(e: ChangeEvent<HTMLInputElement>) => {
                  const value = isNaN(e.target.valueAsNumber) ? null : e.target.valueAsNumber;
                  field.handleChange(value);
                }}
                errorMessage={field.state.meta.errors[0]?.message}
                step={1}
                min={0}
              />
            )}
          </form.Field>

          <form.Field name="adjustment">
            {(field) => (
              <NumberInput
                label="Adjustment"
                name={field.name}
                value={field.state.value ?? undefined}
                onChange={(e: ChangeEvent<HTMLInputElement>) => {
                  const value = isNaN(e.target.valueAsNumber) ? null : e.target.valueAsNumber;
                  field.handleChange(value);
                }}
                errorMessage={field.state.meta.errors[0]?.message}
                step={1}
              />
            )}
          </form.Field>

          <NumberInput label="Used" value={stock.used} disabled hideSpinner />

          <form.Subscribe selector={(state) => [state.values.count, state.values.adjustment]}>
            {([count, adjustment]) => {
              const available = (count ?? 0) - stock.used + (adjustment ?? 0);
              const isNegative = count !== null && adjustment !== null && available < 0;
              return (
                <NumberInput
                  label="Available"
                  value={available}
                  disabled
                  hideSpinner
                  errorMessage={
                    isNegative
                      ? 'Available stock should not be negative. Increase Shipped or the adjustment.'
                      : undefined
                  }
                />
              );
            }}
          </form.Subscribe>
        </div>

        <DialogFooter>
          <form.Subscribe
            selector={(state) => ({
              canSubmit: state.canSubmit,
              isSubmitting: state.isSubmitting,
              available: (state.values.count ?? 0) - stock.used + (state.values.adjustment ?? 0)
            })}
          >
            {({ canSubmit, isSubmitting }) => (
              <Button
                variant="accent"
                onClick={form.handleSubmit}
                disabled={isSubmitting || !canSubmit}
                isLoading={isSubmitting}
              >
                Confirm
              </Button>
            )}
          </form.Subscribe>

          <DialogClose asChild>
            <Button variant="transparent">Cancel</Button>
          </DialogClose>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
